import {
  type FieldError,
  STATUS_CATEGORIES,
  type StatusCategory,
  type WorkflowDraft,
} from '@servicetim/shared';
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@servicetim/ui/components';
import { Plus, Trash2 } from 'lucide-react';
import { useId } from 'react';
import { errorsAt } from '../../projects/lib/field-errors';
import { ColorInput } from '../../projects/ui/color-input';
import { InlineErrors } from '../../projects/ui/inline-errors';
import { ReorderButtons } from '../../projects/ui/reorder-buttons';
import { SimpleSelect } from '../../projects/ui/simple-select';
import { addStatus, moveStatus, removeStatus, renameStatus, updateStatus } from './draft-utils';

export const CATEGORY_LABELS: Record<StatusCategory, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
};

export interface StatusesTableProps {
  draft: WorkflowDraft;
  onChange: (draft: WorkflowDraft) => void;
  errors: readonly FieldError[];
}

/** Editable statuses: name, key, category, portal name, color, initial radio, order, remove. */
export function StatusesTable({ draft, onChange, errors }: StatusesTableProps) {
  const id = useId();
  const usedAsTarget = new Set(draft.transitions.map((t) => t.toStatusId));
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border bg-surface-raised">
        <Table>
          <caption className="sr-only">Statuses</caption>
          <TableHeader className="bg-surface-sunken">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-24">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-44">Key</TableHead>
              <TableHead className="w-40">Category</TableHead>
              <TableHead>Portal name</TableHead>
              <TableHead className="w-56">Color</TableHead>
              <TableHead className="w-20">Initial</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft.statuses.map((status, index) => {
              const rowErrors = errorsAt(errors, `statuses.${index}`);
              const isInitial = draft.initialStatusId === status.id;
              return (
                <TableRow key={status.id} data-state={rowErrors.length > 0 ? 'invalid' : undefined}>
                  <TableCell>
                    <ReorderButtons
                      label={status.name}
                      onUp={
                        index > 0 ? () => onChange(moveStatus(draft, index, index - 1)) : undefined
                      }
                      onDown={
                        index < draft.statuses.length - 1
                          ? () => onChange(moveStatus(draft, index, index + 1))
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Status ${index + 1} name`}
                      value={status.name}
                      maxLength={60}
                      onChange={(e) => onChange(renameStatus(draft, index, e.target.value))}
                      aria-invalid={rowErrors.length > 0 || undefined}
                    />
                    <InlineErrors errors={rowErrors} prefix={`statuses.${index}.`} />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Status ${index + 1} key`}
                      value={status.key}
                      maxLength={40}
                      className="font-mono text-xs"
                      onChange={(e) =>
                        onChange(updateStatus(draft, index, { key: e.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <SimpleSelect
                      aria-label={`Status ${index + 1} category`}
                      value={status.category}
                      options={STATUS_CATEGORIES.map((c) => ({
                        value: c,
                        label: CATEGORY_LABELS[c],
                      }))}
                      onChange={(next) =>
                        next &&
                        onChange(updateStatus(draft, index, { category: next as StatusCategory }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Status ${index + 1} portal name`}
                      value={status.portalName ?? ''}
                      placeholder={status.name}
                      maxLength={60}
                      onChange={(e) =>
                        onChange(
                          updateStatus(draft, index, {
                            portalName: e.target.value === '' ? null : e.target.value,
                          }),
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <ColorInput
                      value={status.color}
                      onChange={(color) => onChange(updateStatus(draft, index, { color }))}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <input
                      type="radio"
                      name={`${id}-initial`}
                      aria-label={`${status.name} is the initial status`}
                      checked={isInitial}
                      onChange={() => onChange({ ...draft, initialStatusId: status.id })}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${status.name}`}
                      disabled={draft.statuses.length <= 1}
                      title={
                        usedAsTarget.has(status.id)
                          ? 'Transitions into this status are removed with it'
                          : isInitial
                            ? 'The next status becomes initial'
                            : undefined
                      }
                      onClick={() => onChange(removeStatus(draft, status.id))}
                    >
                      <Trash2 />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <InlineErrors
        errors={errors.filter((e) => e.path === 'statuses' || e.path === 'initialStatusId')}
      />
      <Button
        variant="outline"
        onClick={() => onChange(addStatus(draft))}
        disabled={draft.statuses.length >= 100}
      >
        <Plus />
        Add status
      </Button>
    </div>
  );
}
